import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { RubricTemplate, RubricTemplateCriterion } from '../src/types';

const rubricTemplatesCollection = collection(db, 'rubricTemplates');

const handleFirestoreError = (error: any, context: string) => {
    console.error(`Firestore error during ${context}:`, error);
    let userMessage = `An error occurred with the database while ${context}.`;

    if (error?.code) {
        switch (error.code) {
            case 'permission-denied':
                userMessage = 'Database permission denied. Please check your Firestore security rules in the Firebase console.';
                break;
            case 'unauthenticated':
                userMessage = 'You are not authenticated. Please log in again.';
                break;
            case 'unavailable':
                userMessage = 'The database service is currently unavailable. Please try again later.';
                break;
        }
    }

    throw new Error(userMessage);
};

export interface RubricTemplatePayload {
    name: string;
    totalMarks: number;
    criteria: RubricTemplateCriterion[];
    sourceAssessmentId?: string;
}

/**
 * Get all rubric templates for a user, sorted by name.
 * Uses a single-field query and client-side sort to avoid composite indexes.
 */
export const getRubricTemplates = async (userId: string): Promise<RubricTemplate[]> => {
    try {
        const q = query(rubricTemplatesCollection, where('userId', '==', userId));
        const snapshot = await getDocs(q);
        const templates = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as RubricTemplate[];
        return templates.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } catch (error) {
        handleFirestoreError(error, 'getting rubric templates');
        return [];
    }
};

export const addRubricTemplate = async (userId: string, data: RubricTemplatePayload): Promise<void> => {
    try {
        await addDoc(rubricTemplatesCollection, {
            ...data,
            userId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        handleFirestoreError(error, 'saving rubric template');
    }
};

/**
 * Update a template's contents and/or name (used for overwrite and rename).
 */
export const updateRubricTemplate = async (templateId: string, data: Partial<RubricTemplatePayload>): Promise<void> => {
    try {
        await updateDoc(doc(db, 'rubricTemplates', templateId), {
            ...data,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        handleFirestoreError(error, 'updating rubric template');
    }
};

export const deleteRubricTemplate = async (templateId: string): Promise<void> => {
    try {
        await deleteDoc(doc(db, 'rubricTemplates', templateId));
    } catch (error) {
        handleFirestoreError(error, 'deleting rubric template');
    }
};
